import React from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import {
	Button,
	CloseButton,
	HookFormInputGroup,
	HookSelectFormGroup,
} from "components";
import { DebounceTimeout } from "globalConstants";
import { EnumItem } from "types/enums/customEnum";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import { required } from "validators/validation";

import styles from "./modal.module.scss";

type UWDecisionModalProps = {
	show?: boolean;
	onHide: () => void;
	title?: string;
	subTitle?: string;
	buttonText?: string;
	onSubmit: Function;
	decisions: EnumItem[];
	defaultValues: {
		decision: EnumItem;
		title: string;
		note: string;
	};
};

const UWDecisionModal = ({
	show = false,
	onHide,
	title = "Approve the application?",
	subTitle = "",
	buttonText = "Approve",
	onSubmit,
	decisions,
	defaultValues,
}: UWDecisionModalProps) => {
	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues,
	});

	const {
		handleSubmit,
		register,
		formState: { errors },
		setValue,
	} = formMethods;

	const handleSubmitForm = values => {
		onHide();
		onSubmit(values);
	};

	const handleChangeDecision = ({ displayName }) => {
		setValue("title", `Underwriting ${displayName}`);

		// if (name === UWDecisions.ApprovedNeedsSponsorBankConcurrence.name) {
		// 	setValue(
		// 		"note",
		// 		ApprovedSponsorBankConcurrenceAlert +
		// 			"\n\n" +
		// 			defaultValues?.note || "",
		// 	);
		// } else {
		// 	setValue("note", defaultValues?.note);
		// }
	};

	return (
		<FormProvider {...formMethods}>
			<Modal
				show={show}
				onHide={onHide}
				animation={false}
				centered
				className={styles.wrapUWDecisionModal}
			>
				<form
					id="uwDecisionForm"
					onSubmit={handleSubmit(handleSubmitForm)}
				>
					<Modal.Header className="pb-0">
						<Modal.Title as="h6" className="text-center mb-3">
							<div>{title}</div>
							{subTitle && (
								<div className={styles.subTitle}>
									{subTitle}
								</div>
							)}
						</Modal.Title>
						<CloseButton onClick={onHide} />
					</Modal.Header>
					<Modal.Body>
						<div className={styles.content}>
							<HookSelectFormGroup
								rules={{
									validate: {
										required,
									},
								}}
								label="Underwriting Decision"
								options={decisions}
								getOptionLabel={option => option.displayName}
								getOptionValue={option => option.name}
								placeholder="Please Select"
								name="decision"
								cacheOptions
								defaultOptions
								debounceTimeout={DebounceTimeout}
								colLabel={{ sm: 12 }}
								colSelect={{ sm: 12 }}
								showRequiredMark
								onChange={handleChangeDecision}
							/>

							<HookFormInputGroup
								name="title"
								register={register}
								label="Note Title"
								colInput={12}
								colLabel={12}
								autoComplete="off"
								validator={{
									required,
								}}
								normalize={trimMidSentenceNormalizer}
								showRequiredMark
								normalizeOnBlur={trimNormalizer}
								errors={errors}
							/>
							<HookFormInputGroup
								name="note"
								register={register}
								label="Body"
								colInput={12}
								colLabel={12}
								autoComplete="off"
								validator={{
									required,
								}}
								normalize={trimMidSentenceNormalizer}
								showRequiredMark
								normalizeOnBlur={trimNormalizer}
								errors={errors}
								as="textarea"
								rows={14}
							/>
						</div>
					</Modal.Body>
					<Modal.Footer className="justify-content-center pt-0 pb-5">
						<Button
							type="submit"
							value={buttonText}
							variant="primary"
						/>
					</Modal.Footer>
				</form>
			</Modal>
		</FormProvider>
	);
};

export default UWDecisionModal;
